'use client';

import React, { createContext, useEffect, useCallback, useState } from 'react';
import Link from 'next/link';
import CountryFAQ from '@/components/country/CountryFAQ';
import GfSectionMark from '@/components/common/GfSectionMark';
import AffiliateLink from '@/components/common/AffiliateLink';
import GfArticleCards from '@/components/common/GfArticleCards';
import GfSubnav from '@/components/common/GfSubnav';
import GfVerticalHero from '@/components/common/GfVerticalHero';
import GfSteps from '@/components/common/GfSteps';
import GfCta from '@/components/common/GfCta';
import GfPromoCodes from '@/components/country/GfPromoCodes';
import EsimPlansLedger from './EsimPlansLedger';
import { CurrencyProvider, CurrencySwitcher, ConvMoney } from '@/components/common/CurrencyContext';
import { ICONS } from '@/components/common/gfIcons';
import { makeCountryDisplay } from '@/lib/content/declension';
import { t } from '@/i18n/translations';

// react-icons (Feather) per eSIM scenario type
const SCENARIO_ICONS = {
    'short-trip': 'calendar',
    'long-trip': 'clock',
    'unlimited': 'zap',
    'messengers': 'messageCircle',
    'with-number': 'phone',
    'regional': 'globe',
    'mobilepay': 'creditCard',
};

// Each "Тарифы на любой случай" card maps to a preset of ledger filters/sort.
const SCENARIO_PRESETS = {
    'short-trip': { cov: 'local', type: 'capped', sortKey: 'days', sortDir: 1 },
    'long-trip': { cov: 'local', type: 'capped', sortKey: 'days', sortDir: -1 },
    'unlimited': { type: 'unlimited' },
    'messengers': { cov: 'local', type: 'capped', sortKey: 'gb', sortDir: 1 },
    'with-number': { calls: 'calls' },
    'regional': { cov: 'regional' },
    'mobilepay': { pay: 'mobile_payments' },
};

export const QuizModalContext = createContext({ openQuiz: () => { } });

export default function ESimPageContent({ content, lang = 'ru', esimArticles = [], background, country = 'montenegro', countryName = null, baseCurrency = 'USD' }) {
    const isRussian = lang === 'ru';
    const heroBg = background || '/images/my-hero.jpg';
    const { cName, cCase } = makeCountryDisplay(countryName, country, isRussian);
    const sp = t(lang, 'servicePage');   // общие тексты сервис-страниц
    const vp = t(lang, 'esimPage');       // специфика eSIM

    const openQuiz = useCallback(() => {
        window.location.href = `/${lang}/${country}/esim/quiz`;
    }, [lang, country]);

    // "Тарифы на любой случай" cards act as ledger filter presets: applying one
    // sets the plans table filters/sort and scrolls down to it.
    const [ledgerPreset, setLedgerPreset] = useState(null);
    const [activeScenario, setActiveScenario] = useState(null);
    const [showAllProviders, setShowAllProviders] = useState(false);
    const applyScenario = useCallback((type) => {
        const p = SCENARIO_PRESETS[type];
        if (!p) return;
        setActiveScenario(type);
        setLedgerPreset((prev) => ({ ...p, n: (prev?.n || 0) + 1 }));
        const el = typeof document !== 'undefined' && document.getElementById('plans');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    // FAQ answer HTML can embed .esim-quiz-link / .esim-device-check-link buttons
    useEffect(() => {
        const handleFAQLinks = (e) => {
            if (e.target.classList.contains('esim-quiz-link')) {
                e.preventDefault();
                window.location.href = `/${lang}/${country}/esim/quiz`;
            }
            if (e.target.classList.contains('esim-device-check-link')) {
                e.preventDefault();
                window.location.href = `/${lang}/${country}/esim/device-check`;
            }
        };
        document.addEventListener('click', handleFAQLinks);
        return () => document.removeEventListener('click', handleFAQLinks);
    }, [lang, country]);

    // Resume an in-progress quiz if one was saved
    useEffect(() => {
        try {
            const savedState = localStorage.getItem('esimQuizState');
            if (savedState) {
                const parsed = JSON.parse(savedState);
                if (parsed && parsed.language === lang && (parsed.currentQuestion > 0 || parsed.showResults)) {
                    openQuiz();
                }
            }
        } catch (error) {
            console.warn('Error checking saved quiz state:', error);
        }
    }, [lang, openQuiz]);

    // Country-unique "local networks & coverage" block (Sanity: country.esimLocalNotes,
    // placeholders resolved server-side) — breaks the near-duplicate templating that
    // kept clone eSIM pages out of Google's index.
    const localNotes = content.localNotes || null;

    const has = {
        providers: content.providers?.length > 0,
        local: Boolean(localNotes),
        features: content.scenarios?.length > 0,
        how: content.steps?.length > 0,
        promocodes: content.promotions?.length > 0,
        faq: content.faq?.length > 0,
        articles: esimArticles?.length > 0,
    };

    const anchors = [
        has.providers && { href: '#providers', label: sp.anchor.providers },
        has.local && { href: '#local', label: localNotes.eyebrow },
        has.features && { href: '#features', label: vp.anchorScenarios },
        { href: '#plans', label: vp.anchorPlans },
        has.how && { href: '#how', label: vp.anchorHow },
        has.promocodes && { href: '#promocodes', label: vp.anchorPromocodes },
        has.faq && { href: '#faq', label: sp.anchor.faq },
        has.articles && { href: '#articles', label: vp.anchorArticles },
    ].filter(Boolean);
    const visibleProviders = showAllProviders ? content.providers : content.providers.slice(0, 3);

    return (
        <QuizModalContext.Provider value={{ openQuiz }}>
          <CurrencyProvider lang={lang} baseCurrency={baseCurrency}>
            <div className="gf-root gf-esim-page">
                {/* ── HERO ─────────────────────────────────────────── */}
                <GfVerticalHero background={heroBg} title={content.title} sub={content.description}>
                    {/* «Посмотреть тарифы» вела в noindex-квиз, а тарифы — сразу под героем (обход 01.10.2026). */}
                    <a className="pri" href="#plans">
                        {content.heroCta || vp.heroCtaFallback} <b>↓</b>
                    </a>
                    <Link className="sec" href={`/${lang}/${country}/esim/quiz`}>
                        {vp.heroQuiz}
                    </Link>
                </GfVerticalHero>

                {/* ── SUBNAV ───────────────────────────────────────── */}
                <GfSubnav lang={lang} leaf={vp.leaf} anchors={anchors}
                    crumbs={[cName && { href: `/${lang}/${country}`, label: cName }]} />

                {/* ── 01 · RECOMMENDED PLANS + FULL COMPARISON ──── */}
                <section className="gf-sec" id="plans">
                    <GfSectionMark eyebrow={vp.plansEyebrow} tag={cName ? `${cName}` : ''} title={vp.plansTitle} />
                    <p className="gf-lead">{vp.plansLead}</p>
                    <div className="gf-cur-bar">
                        <CurrencySwitcher />
                    </div>
                    <EsimPlansLedger
                        lang={lang}
                        country={country}
                        countryName={cName}
                        offers={content.ledgerOffers || []}
                        preset={ledgerPreset}
                    />
                </section>

                {/* ── 02 · PROVIDERS ───────────────────────────────── */}
                {has.providers && (
                    <section className="gf-sec" id="providers" style={{ paddingTop: 0 }}>
                        <GfSectionMark eyebrow={sp.eyebrow.providers} tag={content.providers.length} title={vp.providersTitle(cName ? (isRussian ? cCase('genitive') : cName) : '')} />
                        <p className="gf-lead">
                            {vp.providersLead}
                        </p>

                        <div className="gf-grid gf-grid-center">
                            {visibleProviders.map((provider) => {
                                const buyLabel = vp.buy;
                                return (
                                    <article key={provider.slug} className={`pc ${provider.bestPick ? 'pick' : ''}`}>
                                        {/* full-card click-through → provider detail page (stretched link;
                                            sits below the footer so the Buy/Details buttons stay clickable) */}
                                        <Link className="pc-cover" href={`/${lang}/${country}/esim/${provider.slug}`} aria-label={`${provider.name} — ${sp.details}`} />
                                        {provider.bestPick && <span className="stamp">{vp.pick}</span>}
                                        <div className="logo">
                                            {provider.logo
                                                ? <img src={provider.logo} alt={provider.name} />
                                                : <span className="wm">{provider.name}</span>}
                                        </div>
                                        <div className="pc-body">
                                            <h3>{provider.name}</h3>
                                            {provider.description && <p className="vd">{provider.description}</p>}
                                        </div>
                                        <div className="ft">
                                            <span className="pr">
                                                {provider.fromPriceAmount != null
                                                    ? (<><span className="f">{vp.from}</span> <ConvMoney amount={provider.fromPriceAmount} currency={provider.fromPriceCurrency} /></>)
                                                    : provider.fromPrice && (<><span className="f">{vp.from}</span> {provider.fromPrice}</>)}
                                            </span>
                                            <div className="gf-prov-actions">
                                                {provider.site && (
                                                    <AffiliateLink href={provider.site} provider={provider.slug} vertical="esim" country={country} position="listing_card" price={provider.fromPrice} className="buy">
                                                        {buyLabel}
                                                    </AffiliateLink>
                                                )}
                                                <Link className="visit" href={`/${lang}/${country}/esim/${provider.slug}`}>
                                                    {sp.details}
                                                </Link>
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                        {content.providers.length > 3 && (
                            <button type="button" className="gf-showall" onClick={() => setShowAllProviders((shown) => !shown)}>
                                {showAllProviders ? vp.hideProviders : vp.showAllProviders(content.providers.length)}
                            </button>
                        )}
                    </section>
                )}

                {/* ── 01b · LOCAL NETWORKS (country-unique) ────────── */}
                {has.local && (
                    <section className="gf-sec gf-localnet" id="local" style={{ paddingTop: 0 }}>
                        <GfSectionMark eyebrow={localNotes.eyebrow} tag={cName || ''} title={localNotes.heading} />
                        <p className="gf-lead">{localNotes.intro}</p>
                        <ul className="gf-localnet-list">
                            {localNotes.points.map((point, i) => (
                                <li key={i}>{point}</li>
                            ))}
                        </ul>
                        {localNotes.pricing && <p className="gf-localnet-pricing">{localNotes.pricing}</p>}
                        {localNotes.outro && <p className="gf-localnet-outro">{localNotes.outro}</p>}
                    </section>
                )}

                {/* ── QUIZ BAND ────────────────────────────────────── */}
                <aside className="gf-quizband">
                    <div className="in">
                        <div className="tx">
                            <span className="ek">{vp.quizEyebrow}</span>
                            <h2>{vp.quizTitle}</h2>
                            <p>{vp.quizText}</p>
                        </div>
                        <Link className="q-cta" href={`/${lang}/${country}/esim/quiz`}>
                            {vp.quizCta}
                        </Link>
                    </div>
                </aside>

                {/* ── 02 · SCENARIOS ───────────────────────────────── */}
                {has.features && (
                    <section className="gf-sec" id="features" style={{ paddingTop: 0 }}>
                        <GfSectionMark eyebrow={vp.scenariosEyebrow} tag={vp.scenariosTag} title={vp.scenariosTitle} />
                        <p className="gf-lead">{vp.scenariosLead}</p>
                        <div className="gf-scen cols-3">
                            {content.scenarios.map((scenario) => (
                                <button
                                    type="button"
                                    className={`sc sp${activeScenario === scenario.type ? ' is-active' : ''}`}
                                    key={scenario.type}
                                    onClick={() => applyScenario(scenario.type)}
                                >
                                    <span className="n">{scenario.ix || `// ${scenario.type}`}</span>
                                    <div className="ri-row">{ICONS[SCENARIO_ICONS[scenario.type]] || ICONS.compass}<h3>{scenario.title}</h3></div>
                                    <p>{scenario.description}</p>
                                    <span className="go">{scenario.cta || vp.scenarioCtaFallback}</span>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {/* ── 03 · HOW IT WORKS ────────────────────────────── */}
                {has.how && (
                    <section className="gf-sec" id="how" style={{ paddingTop: 0 }}>
                        <GfSectionMark eyebrow={sp.eyebrow.guide} tag={`${content.steps.length} ${sp.stepsWord(content.steps.length)}`} title={vp.howTitle} />
                        <p className="gf-lead">{vp.howLead}</p>
                        <GfSteps steps={content.steps} />
                    </section>
                )}

                {/* ── 04 · PROMO CODES ─────────────────────────────── */}
                {has.promocodes && (
                    <section className="gf-sec" id="promocodes" style={{ paddingTop: 0 }}>
                        <GfSectionMark eyebrow={sp.eyebrow.discounts} tag={sp.tag.current} title={vp.promosTitle} allHref={`/${lang}/${country}/esim/promotions`} allLabel={vp.promosAllLabel} />
                        <p className="gf-lead">{vp.promosLead}</p>
                        <GfPromoCodes promotions={content.promotions} lang={lang} country={country} vertical="esim" />
                    </section>
                )}

                {/* ── 05 · FAQ ─────────────────────────────────────── */}
                {has.faq && (
                    <section className="gf-sec" id="faq" style={{ paddingTop: 0 }}>
                        <GfSectionMark eyebrow={sp.eyebrow.qa} tag={sp.tag.faq} title={sp.popularQuestions} />
                        <p className="gf-lead">{vp.faqLead}</p>
                        <CountryFAQ faqItems={content.faq} lang={lang} variant="gf" />
                        {content.faqLink && (
                            <div style={{ marginTop: 26 }}>
                                <Link className="gf-alllink" href={content.faqLink.href}>{content.faqLink.text}</Link>
                            </div>
                        )}
                    </section>
                )}

                {/* ── 06 · ARTICLES ────────────────────────────────── */}
                {has.articles && (
                    <section className="gf-sec" id="articles" style={{ paddingTop: 0 }}>
                        <GfSectionMark eyebrow={sp.eyebrow.resources} tag={sp.tag.blog} title={vp.articlesTitle} allHref={`/${lang}/blog/category/esim`} allLabel={sp.allArticles} />
                        <p className="gf-lead">{vp.articlesLead}</p>
                        <GfArticleCards posts={esimArticles} lang={lang} variant="bento" bucket />
                    </section>
                )}

                {/* ── CTA ──────────────────────────────────────────── */}
                <GfCta
                    eyebrow={vp.ctaEyebrow}
                    title={vp.ctaTitle(cName ? (isRussian ? cCase('prepositional') : cName) : '')}
                >
                    <Link className="gf-btn" href={`/${lang}/${country}/esim/quiz`}>
                        {vp.ctaBtn}
                    </Link>
                </GfCta>
            </div>
          </CurrencyProvider>
        </QuizModalContext.Provider>
    );
}
